import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import '../styles/Docs.css';
import '../styles/Home.css';
import logo from '../assets/logo/light.png';
import { LayoutDashboard, Menu, X } from 'lucide-react';
import UserMenu from '../components/UserMenu';

const API_BASE = process.env.REACT_APP_API_BASE;

/* ── tiny helpers ────────────────────────────────────────────── */

function T({ c, children }) {
  return <span className={`tok-${c}`}>{children}</span>;
}

function CodeBlock({ lang, children }) {
  return (
    <div className="code-block">
      <div className="code-header"><span className="code-lang">{lang}</span></div>
      <pre><code>{children}</code></pre>
    </div>
  );
}

function Callout({ warn, children }) {
  return <div className={`callout${warn ? ' warn' : ''}`}>{children}</div>;
}

function EndpointCard({ method, path, children }) {
  return (
    <div className="endpoint-card">
      <div className="endpoint-header">
        <span className={`method ${method}`}>{method}</span>
        <span className="endpoint-path">{path}</span>
      </div>
      <div className="endpoint-body">{children}</div>
    </div>
  );
}

function Badge({ color, children }) {
  return <span className={`badge ${color}`}>{children}</span>;
}

function DocTable({ headers, rows }) {
  return (
    <div className="table-wrap">
      <table className="docs-table">
        <thead>
          <tr>{headers.map(h => <th key={h}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── sidebar sections ───────────────────────────────────────── */

const SECTIONS = [
  { group: 'Getting Started', links: [
    { id: 'overview',       label: 'Overview' },
    { id: 'authentication', label: 'Authentication' },
  ]},
  { group: 'Webhooks', links: [
    { id: 'webhook-new-order',      label: 'New Order' },
    { id: 'webhook-status-change',  label: 'Order Update' },
  ]},
  { group: 'Endpoints', links: [
    { id: 'order-create', label: 'POST /order/create' },
    { id: 'order-info', label: 'POST /order/info' },
    { id: 'order-push', label: 'POST /order/push' },
  ]},
  { group: 'Concepts', links: [
    { id: 'merchant-refs', label: 'Merchant Tokens' },
    { id: 'status-flow',   label: 'Status Flow' },
  ]},
  { group: 'Reference', links: [
    { id: 'errors',   label: 'Error Reference' },
    { id: 'examples', label: 'Examples' },
  ]},
];

/* ── main component ─────────────────────────────────────────── */

export default function Docs() {
  const [active, setActive] = useState('overview');
  const [menuOpen, setMenuOpen] = useState(false);
  const mainRef = useRef(null);
  const { token, account, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { openModal } = useModal();
  const [signingOut, setSigningOut] = useState(false);

  async function doSignOut() {
    setSigningOut(true);
    try {
      await fetch(`${API_BASE}/auth/sign_out`, {
        method: 'POST',
        headers: { AUTH: token },
      });
    } catch { /* still log out locally */ }
    logout();
    navigate('/login');
  }

  function handleSignOut() {
    openModal({
      title: 'Sign out',
      text: 'Are you sure you want to sign out?',
      confirm: { label: 'Sign out', danger: true, onClick: doSignOut },
      cancel: { label: 'Stay signed in' },
    });
  }

  useEffect(() => {
    const all = SECTIONS.flatMap(s => s.links.map(l => l.id));
    const observer = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) setActive(e.target.id);
      });
    }, { rootMargin: '-20% 0px -70% 0px', threshold: 0 });
    all.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="dash-layout">

      {/* ── Mobile nav overlay ── */}
      {menuOpen && <div className="mobile-overlay" onClick={() => setMenuOpen(false)} />}

      {/* ── sidebar ── */}
      <aside className={`sidebar${menuOpen ? ' sidebar--open' : ''}`}>
        <Link to={isAuthenticated ? '/dashboard' : '/'} className="sidebar-brand" style={{ textDecoration: 'none' }}>
          <img src={logo} alt="" />
          <div>
            <div className="sidebar-brand-text">Ride Eats</div>
            <div className="sidebar-brand-sub">API Reference</div>
          </div>
          <button className="sidebar-close" onClick={e => { e.preventDefault(); setMenuOpen(false); }}>
            <X size={18} />
          </button>
        </Link>

        <nav className="sidebar-nav">
          {SECTIONS.map(sec => (
            <div key={sec.group}>
              <div className="sidebar-section-label">{sec.group}</div>
              {sec.links.map(l => (
                <a
                  key={l.id}
                  href={`#${l.id}`}
                  className={`sidebar-item${active === l.id ? ' active' : ''}`}
                  style={{ textDecoration: 'none' }}
                  onClick={e => {
                    e.preventDefault();
                    setMenuOpen(false);
                    const el = document.getElementById(l.id);
                    if (el) {
                      const top = el.getBoundingClientRect().top + window.scrollY - 72;
                      window.scrollTo({ top, behavior: 'smooth' });
                    }
                  }}
                >
                  {l.label}
                </a>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <Link to="/dashboard" className="sidebar-item" style={{ textDecoration: 'none' }} onClick={() => setMenuOpen(false)}>
            <LayoutDashboard size={14} className="sicon-svg" />Dashboard
          </Link>
        </div>
      </aside>

      {/* ── content ── */}
      <div className="dash-main">
        <div className="dash-topbar">
          <div className="topbar-left">
            <button className="hamburger-btn" onClick={() => setMenuOpen(o => !o)} aria-label="Menu">
              <Menu size={20} />
            </button>
            <span className="dash-page-title">API Reference</span>
          </div>
          <div className="topbar-actions">
            <button className="btn-sm">v1.0</button>
            {isAuthenticated && (
              <Link to="/dashboard" className="btn-sm" style={{ textDecoration: 'none' }}>Dashboard</Link>
            )}
            {isAuthenticated ? (
              <UserMenu name={account?.name} onSignOut={handleSignOut} signingOut={signingOut} />
            ) : (
              <Link to="/login" className="btn-sm" style={{ textDecoration: 'none' }}>Sign in</Link>
            )}
          </div>
        </div>
        <div className="docs-content" ref={mainRef}>

        {/* OVERVIEW */}
        <section id="overview">
          <h2>Overview</h2>
          <p>
            The Sync API lets your POS or external system fetch order details and push status updates directly into
            the Ride Eats platform. Authentication is key-based and all operations are scoped to the places linked
            to your API key.
          </p>
          <div className="base-url">
            <span className="label">Base URL</span>
            https://ride-eats.com/api2/sync
          </div>
          <Callout>
            All requests must be <code>POST</code> with a JSON body and{' '}
            <code>Content-Type: application/json</code>. The <code>API</code> header is required on every request.
          </Callout>
        </section>

        {/* AUTHENTICATION */}
        <section id="authentication">
          <h2>Authentication</h2>
          <p>
            Pass your API key in the <code>API</code> HTTP header. Keys are issued per place and restrict access
            to orders belonging to your linked place(s).
          </p>
          <div className="auth-block">
            <div className="row"><span className="key">API</span><span className="val">your-api-key-here</span></div>
            <div className="row"><span className="key">Content-Type</span><span className="val">application/json</span></div>
          </div>
          <p>If the key is missing, expired, or invalid:</p>
          <CodeBlock lang="JSON">
            <T c="p">{'{'}</T>{'\n'}
            {'  '}<T c="k">"success"</T><T c="p">:</T>{'  '}<T c="b">false</T><T c="p">,</T>{'\n'}
            {'  '}<T c="k">"response"</T><T c="p">:</T>{' '}<T c="s">"Invalid or inactive API key."</T>{'\n'}
            <T c="p">{'}'}</T>
          </CodeBlock>
        </section>

        {/* WEBHOOK — NEW ORDER */}
        <section id="webhook-new-order">
          <h2>Webhooks: Receiving Events</h2>
          <p>
            When configured, Ride Eats POSTs events to your endpoint URL. Your server must respond with HTTP{' '}
            <code>200</code> within 5 seconds to acknowledge delivery.
          </p>
          <Callout>
            Register your webhook URL in the Ride Eats dashboard under{' '}
            <strong>API Keys → Webhook URL</strong>. All events are sent as <code>POST</code> with{' '}
            <code>Content-Type: application/json</code>.
          </Callout>

          <EndpointCard method="POST" path="your-endpoint ← new order">
            <p className="endpoint-desc">
              Fired when a customer places a new order. The <code>data.order</code> object is identical to the{' '}
              <a href="#order-info">POST /order/info</a> response, with <code>status</code> always{' '}
              <code>pending</code>.
            </p>

            <h3>Payload</h3>
            <CodeBlock lang="JSON">
              <T c="p">{'{'}</T>{'\n'}
              {'  '}<T c="k">"event"</T><T c="p">:</T>{'  '}<T c="s">"order.new"</T><T c="p">,</T>{'\n'}
              {'  '}<T c="k">"data"</T><T c="p">: {'{'}</T>{'\n'}
              {'    '}<T c="k">"merchant_token"</T><T c="p">:</T>{' '}<T c="s">"downtown"</T><T c="p">,</T>{'    '}<T c="c">{'// your alias for this merchant — see Merchant Tokens'}</T>{'\n'}
              {'    '}<T c="k">"order"</T><T c="p">: {'{'}</T>{'\n'}
              {'      '}<T c="k">"id"</T><T c="p">:</T>{'              '}<T c="n">489727</T><T c="p">,</T>{'\n'}
              {'      '}<T c="k">"placed_on"</T><T c="p">:</T>{'      '}<T c="n">1745412125</T><T c="p">,</T>{'\n'}
              {'      '}<T c="k">"status"</T><T c="p">:</T>{'         '}<T c="s">"pending"</T><T c="p">,</T>{'     '}<T c="c">{'// always pending on new order'}</T>{'\n'}
              {'      '}<T c="k">"status_updated"</T><T c="p">:</T>{' '}<T c="n">1745412125</T><T c="p">,</T>{'\n'}
              {'      '}<T c="k">"type"</T><T c="p">:</T>{'           '}<T c="s">"delivery"</T><T c="p">,</T>{'\n'}
              {'      '}<T c="k">"when"</T><T c="p">:</T>{'           '}<T c="s">"now"</T><T c="p">,</T>{'\n'}
              {'      '}<T c="k">"payment"</T><T c="p">: {'{'}</T>{' '}<T c="k">"method"</T><T c="p">:</T>{' '}<T c="s">"cash"</T><T c="p">,</T>{' '}<T c="k">"amount"</T><T c="p">:</T>{' '}<T c="n">600</T>{' '}<T c="p">{'}'}</T><T c="p">,</T>{'\n'}
              {'      '}<T c="k">"customer"</T><T c="p">: {'{'}</T>{' '}<T c="k">"name"</T><T c="p">:</T>{' '}<T c="s">"John Doe"</T><T c="p">,</T>{' '}<T c="k">"phone"</T><T c="p">:</T>{' '}<T c="s">"+5978123456"</T>{' '}<T c="p">{'}'}</T><T c="p">,</T>{'\n'}
              {'      '}<T c="k">"delivery"</T><T c="p">: {'{'}</T>{'\n'}
              {'        '}<T c="k">"address"</T><T c="p">:</T>{'  '}<T c="s">"Henck Arronstraat 12"</T><T c="p">,</T>{'\n'}
              {'        '}<T c="k">"coords"</T><T c="p">: {'{'}</T>{' '}<T c="k">"lat"</T><T c="p">:</T>{' '}<T c="n">5.8332</T><T c="p">,</T>{' '}<T c="k">"lng"</T><T c="p">:</T>{' '}<T c="n">-55.1668</T>{' '}<T c="p">{'}'}</T><T c="p">,</T>{'\n'}
              {'        '}<T c="k">"comments"</T><T c="p">:</T>{' '}<T c="s">"Ring the bell"</T><T c="p">,</T>{'\n'}
              {'        '}<T c="k">"fee"</T><T c="p">:</T>{'      '}<T c="n">80</T><T c="p">,</T>{'\n'}
              {'        '}<T c="k">"distance"</T><T c="p">:</T>{' '}<T c="n">2.4</T>{'\n'}
              {'      '}<T c="p">{'}'}</T><T c="p">,</T>{'\n'}
              {'      '}<T c="k">"biker"</T><T c="p">:</T>{'  '}<T c="b">null</T><T c="p">,</T>{'               '}<T c="c">{'// always null on new order'}</T>{'\n'}
              {'      '}<T c="k">"items"</T><T c="p">: [</T>{'\n'}
              {'        '}<T c="p">{'{'}</T>{'\n'}
              {'          '}<T c="k">"title"</T><T c="p">:</T>{'        '}<T c="s">"Chicken Burger"</T><T c="p">,</T>{'\n'}
              {'          '}<T c="k">"key"</T><T c="p">:</T>{'          '}<T c="s">"chicken-burger"</T><T c="p">,</T>{'  '}<T c="c">{'// null if no custom key set'}</T>{'\n'}
              {'          '}<T c="k">"variant_key"</T><T c="p">:</T>{'   '}<T c="s">"large"</T><T c="p">,</T>{'    '}<T c="c">{'// null if no custom key set'}</T>{'\n'}
              {'          '}<T c="k">"variant"</T><T c="p">:</T>{'      '}<T c="s">"Large"</T><T c="p">,</T>{'\n'}
              {'          '}<T c="k">"qty"</T><T c="p">:</T>{'          '}<T c="n">2</T><T c="p">,</T>{'\n'}
              {'          '}<T c="k">"total"</T><T c="p">:</T>{'        '}<T c="n">140</T><T c="p">,</T>{'\n'}
              {'          '}<T c="k">"modifiers"</T><T c="p">: [</T>{'\n'}
              {'            '}<T c="p">{'{'}</T>{'\n'}
              {'              '}<T c="k">"group"</T><T c="p">:</T>{'     '}<T c="s">"Extras"</T><T c="p">,</T>{'\n'}
              {'              '}<T c="k">"group_key"</T><T c="p">:</T>{' '}<T c="s">"extras"</T><T c="p">,</T>{'  '}<T c="c">{'// null if no custom key set'}</T>{'\n'}
              {'              '}<T c="k">"choices"</T><T c="p">: [</T>{'\n'}
              {'                '}<T c="p">{'{'}</T>{' '}<T c="k">"title"</T><T c="p">:</T>{' '}<T c="s">"Extra Sauce"</T><T c="p">,</T>{' '}<T c="k">"key"</T><T c="p">:</T>{' '}<T c="s">"extra-sauce"</T>{' '}<T c="p">{'}'}</T>{'\n'}
              {'              '}<T c="p">]</T>{'\n'}
              {'            '}<T c="p">{'}'}</T>{'\n'}
              {'          '}<T c="p">]</T><T c="p">,</T>{'\n'}
              {'          '}<T c="k">"instructions"</T><T c="p">:</T>{' '}<T c="s">"Well done please"</T>{'\n'}
              {'        '}<T c="p">{'}'}</T>{'\n'}
              {'      '}<T c="p">{'}'}</T>{'\n'}
              {'    '}<T c="p">{'}'}</T>{'\n'}
              {'  '}<T c="p">{'}'}</T>{'\n'}
              <T c="p">{'}'}</T>
            </CodeBlock>

            <h3>Notes</h3>
            <DocTable
              headers={['Field', 'Value on new order']}
              rows={[
                ['event', <><code>order.new</code></>],
                ['data.merchant_token', <>Your alias for the merchant this order belongs to — see <a href="#merchant-refs">Merchant Tokens</a></>],
                ['data.order.status', <>Always <code>pending</code></>],
                ['data.order.biker', <>Always <code>null</code></>],
                ['data.order', <>Full object — identical structure to <a href="#order-info">POST /order/info</a></>],
              ]}
            />
          </EndpointCard>
        </section>

        {/* WEBHOOK — ORDER UPDATE */}
        <section id="webhook-status-change">
          <h2>Webhook: Order Update</h2>
          <EndpointCard method="POST" path="your-endpoint ← order update">
            <p className="endpoint-desc">
              Fired whenever an order status changes — whether triggered by the restaurant, a biker, or the
              platform. Internal statuses <code>readydel</code> and <code>readypick</code> are both normalised to{' '}
              <code>ready</code> in every webhook payload.
            </p>

            <h3>Payload</h3>
            <CodeBlock lang="JSON">
              <T c="p">{'{'}</T>{'\n'}
              {'  '}<T c="k">"event"</T><T c="p">:</T>{'  '}<T c="s">"order.update"</T><T c="p">,</T>{'\n'}
              {'  '}<T c="k">"data"</T><T c="p">: {'{'}</T>{'\n'}
              {'    '}<T c="k">"merchant_token"</T><T c="p">:</T>{' '}<T c="s">"downtown"</T><T c="p">,</T>{'    '}<T c="c">{'// your alias for this merchant — see Merchant Tokens'}</T>{'\n'}
              {'    '}<T c="k">"order_id"</T><T c="p">:</T>{'        '}<T c="n">489727</T><T c="p">,</T>{'\n'}
              {'    '}<T c="k">"previous_status"</T><T c="p">:</T>{' '}<T c="s">"pending"</T><T c="p">,</T>{'\n'}
              {'    '}<T c="k">"new_status"</T><T c="p">:</T>{'      '}<T c="s">"accepted"</T><T c="p">,</T>{'\n'}
              {'    '}<T c="k">"status_updated"</T><T c="p">:</T>{'  '}<T c="n">1745413000</T><T c="p">,</T>{'\n'}
              {'    '}<T c="k">"extras"</T><T c="p">: {'{'}</T>{'                        '}<T c="c">{'// null when no extra args were sent'}</T>{'\n'}
              {'      '}<T c="k">"time"</T><T c="p">:</T>{' '}<T c="n">25</T>{'                        '}<T c="c">{'// estimated prep time in minutes'}</T>{'\n'}
              {'    '}<T c="p">{'}'}</T>{'\n'}
              {'  '}<T c="p">{'}'}</T>{'\n'}
              <T c="p">{'}'}</T>
            </CodeBlock>

            <h3>Extras per status</h3>
            <DocTable
              headers={['Status', 'extras fields', 'Description']}
              rows={[

                [<Badge color="accepted">accepted</Badge>, <><code>time</code> <span style={{color:'var(--muted)',fontSize:'12px'}}>(optional)</span></>, 'Estimated prep time in minutes. null if not provided'],
                [<Badge color="readydel">ready</Badge>, '—', <>extras is <code>null</code></>],
                [<Badge color="ontheway">ontheway</Badge>, '—', <>extras is <code>null</code></>],
                [<Badge color="completed">completed</Badge>, '—', <>extras is <code>null</code></>],
              ]}
            />

            <h3>Status normalisation</h3>
            <p>
              The <code>new_status</code> and <code>previous_status</code> fields always use the external
              vocabulary — the same values accepted by <a href="#order-push">POST /order/push</a>.
            </p>
            <DocTable
              headers={['Internal value', 'Sent in webhook as']}
              rows={[
                [<code>pending</code>,   <Badge color="pending">pending</Badge>],
                [<code>accepted</code>,  <Badge color="accepted">accepted</Badge>],
                [<code>readydel</code>,  <Badge color="readydel">ready</Badge>],
                [<code>readypick</code>, <Badge color="readypick">ready</Badge>],
                [<code>ontheway</code>,  <Badge color="ontheway">ontheway</Badge>],
                [<code>completed</code>, <Badge color="completed">completed</Badge>],
              ]}
            />

            <Callout>
              To get the full updated order after a status change, call{' '}
              <a href="#order-info">POST /order/info</a> with the <code>order_id</code>.
            </Callout>
          </EndpointCard>
        </section>

        {/* /order/create */}
        <section id="order-create">
          <h2>POST /order/create</h2>
          <EndpointCard method="POST" path="https://ride-eats.com/api2/sync/order/create">
            <p className="endpoint-desc">
              Create a Ride Eats order from your own system. Send only your merchant token and custom menu keys;
              Ride Eats resolves the real menu data, calculates item totals, inserts the order, and returns the
              platform order object.
            </p>

            <Callout>
              <code>external_order_id</code> is required and idempotent. If you retry the same request with the
              same value, the API returns the existing Ride Eats order instead of creating a duplicate.
            </Callout>

            <h3>Request: delivery</h3>
            <CodeBlock lang="JSON">
              <T c="p">{'{'}</T>{'\n'}
              {'  '}<T c="k">"external_order_id"</T><T c="p">:</T>{' '}<T c="s">"pos-100928"</T><T c="p">,</T>{'\n'}
              {'  '}<T c="k">"merchant_token"</T><T c="p">:</T>{'    '}<T c="s">"downtown"</T><T c="p">,</T>{'\n'}
              {'  '}<T c="k">"type"</T><T c="p">:</T>{'              '}<T c="s">"delivery"</T><T c="p">,</T>{'\n'}
              {'  '}<T c="k">"when"</T><T c="p">:</T>{'              '}<T c="s">"now"</T><T c="p">,</T>{'\n'}
              {'  '}<T c="k">"payment"</T><T c="p">: {'{'}</T>{' '}<T c="k">"method"</T><T c="p">:</T>{' '}<T c="s">"cash"</T>{' '}<T c="p">{'}'}</T><T c="p">,</T>{'\n'}
              {'  '}<T c="k">"customer"</T><T c="p">: {'{'}</T>{'\n'}
              {'    '}<T c="k">"name"</T><T c="p">:</T>{'  '}<T c="s">"John Doe"</T><T c="p">,</T>{'\n'}
              {'    '}<T c="k">"phone"</T><T c="p">:</T>{' '}<T c="s">"+5978123456"</T>{'\n'}
              {'  '}<T c="p">{'}'}</T><T c="p">,</T>{'\n'}
              {'  '}<T c="k">"delivery"</T><T c="p">: {'{'}</T>{'\n'}
              {'    '}<T c="k">"address"</T><T c="p">:</T>{'  '}<T c="s">"Henck Arronstraat 12"</T><T c="p">,</T>{'\n'}
              {'    '}<T c="k">"coords"</T><T c="p">: {'{'}</T>{' '}<T c="k">"lat"</T><T c="p">:</T>{' '}<T c="n">5.8332</T><T c="p">,</T>{' '}<T c="k">"lng"</T><T c="p">:</T>{' '}<T c="n">-55.1668</T>{' '}<T c="p">{'}'}</T><T c="p">,</T>{'\n'}
              {'    '}<T c="k">"comments"</T><T c="p">:</T>{' '}<T c="s">"Ring the bell"</T>{'\n'}
              {'  '}<T c="p">{'}'}</T><T c="p">,</T>{'\n'}
              {'  '}<T c="k">"items"</T><T c="p">: [</T>{'\n'}
              {'    '}<T c="p">{'{'}</T>{'\n'}
              {'      '}<T c="k">"key"</T><T c="p">:</T>{'         '}<T c="s">"chicken-burger"</T><T c="p">,</T>{'\n'}
              {'      '}<T c="k">"variant_key"</T><T c="p">:</T>{' '}<T c="s">"large"</T><T c="p">,</T>{'\n'}
              {'      '}<T c="k">"qty"</T><T c="p">:</T>{'         '}<T c="n">2</T><T c="p">,</T>{'\n'}
              {'      '}<T c="k">"modifiers"</T><T c="p">: [</T>{'\n'}
              {'        '}<T c="p">{'{'}</T>{'\n'}
              {'          '}<T c="k">"group_key"</T><T c="p">:</T>{' '}<T c="s">"extras"</T><T c="p">,</T>{'\n'}
              {'          '}<T c="k">"choices"</T><T c="p">:</T>{'   '}<T c="p">[</T><T c="s">"extra-sauce"</T><T c="p">]</T>{'\n'}
              {'        '}<T c="p">{'}'}</T>{'\n'}
              {'      '}<T c="p">]</T><T c="p">,</T>{'\n'}
              {'      '}<T c="k">"instructions"</T><T c="p">:</T>{' '}<T c="s">""</T>{'\n'}
              {'    '}<T c="p">{'}'}</T>{'\n'}
              {'  '}<T c="p">]</T>{'\n'}
              <T c="p">{'}'}</T>
            </CodeBlock>

            <h3>Response</h3>
            <CodeBlock lang="JSON">
              <T c="p">{'{'}</T>{'\n'}
              {'  '}<T c="k">"success"</T><T c="p">:</T>{'  '}<T c="b">true</T><T c="p">,</T>{'\n'}
              {'  '}<T c="k">"response"</T><T c="p">:</T>{' '}<T c="s">"Order created."</T><T c="p">,</T>{'\n'}
              {'  '}<T c="k">"data"</T><T c="p">: {'{'}</T>{'\n'}
              {'    '}<T c="k">"id"</T><T c="p">:</T>{'                '}<T c="n">489800</T><T c="p">,</T>{'\n'}
              {'    '}<T c="k">"external_order_id"</T><T c="p">:</T>{' '}<T c="s">"pos-100928"</T><T c="p">,</T>{'\n'}
              {'    '}<T c="k">"merchant_token"</T><T c="p">:</T>{'    '}<T c="s">"downtown"</T><T c="p">,</T>{'\n'}
              {'    '}<T c="k">"order"</T><T c="p">: {'{'}</T>{' '}<T c="c">{'// same structure as POST /order/info'}</T>{' '}<T c="p">{'}'}</T>{'\n'}
              {'  '}<T c="p">{'}'}</T>{'\n'}
              <T c="p">{'}'}</T>
            </CodeBlock>

            <h3>Fields</h3>
            <DocTable
              headers={['Field', 'Required', 'Description']}
              rows={[
                ['external_order_id', 'yes', 'Your order ID. Used for safe retries without duplicate orders.'],
                ['merchant_token', 'yes', <>The merchant alias linked to your API key. See <a href="#merchant-refs">Merchant Tokens</a></>],
                ['type', 'yes', <><code>delivery</code> or <code>pickup</code></>],
                ['when', 'no', <><code>now</code> or <code>scheduled</code>. Defaults to <code>now</code></>],
                ['customer.name', 'yes', 'Customer display name'],
                ['customer.phone', 'yes', 'Customer phone number'],
                ['delivery', 'delivery only', 'Address, coords, and comments. Not sent for pickup orders.'],
                ['items[].key', 'yes', 'Custom item key configured in Menu Items'],
                ['items[].variant_key', 'no', 'Custom variant key when the item has a selected size/variant'],
                ['items[].modifiers[].group_key', 'no', 'Custom modifier group key'],
                ['items[].modifiers[].choices[]', 'no', 'Array of custom modifier choice keys'],
              ]}
            />
          </EndpointCard>
        </section>

        {/* /order/info */}
        <section id="order-info">
          <h2>POST /order/info</h2>
          <EndpointCard method="POST" path="https://ride-eats.com/api2/sync/order/info">
            <p className="endpoint-desc">
              Fetch full details for one or multiple orders. Orders must belong to a place linked to your API key.
            </p>

            <h3>Request: single order</h3>
            <CodeBlock lang="JSON">
              <T c="p">{'{'}</T>{'\n'}
              {'  '}<T c="k">"id"</T><T c="p">:</T>{' '}<T c="n">489727</T>{'\n'}
              <T c="p">{'}'}</T>
            </CodeBlock>

            <h3>Request: batch <span style={{fontSize:'13px',color:'var(--muted)',fontWeight:400}}>(max 50)</span></h3>
            <CodeBlock lang="JSON">
              <T c="p">{'{'}</T>{'\n'}
              {'  '}<T c="k">"ids"</T><T c="p">:</T>{' '}<T c="p">[</T><T c="n">489727</T><T c="p">,</T>{' '}<T c="n">489728</T><T c="p">,</T>{' '}<T c="n">489730</T><T c="p">]</T>{'\n'}
              <T c="p">{'}'}</T>
            </CodeBlock>

            <h3>Response</h3>
            <p style={{fontSize:'13.5px'}}>
              For a single <code>id</code>, the data key is <code>order</code> (object). For <code>ids</code>,
              it is <code>orders</code> (array). The <code>merchant_token</code> field is always present and
              contains the alias you assigned to this merchant when linking it to your API key.
            </p>
            <CodeBlock lang="JSON">
              <T c="p">{'{'}</T>{'\n'}
              {'  '}<T c="k">"success"</T><T c="p">:</T>{'  '}<T c="b">true</T><T c="p">,</T>{'\n'}
              {'  '}<T c="k">"response"</T><T c="p">:</T>{' '}<T c="s">"Order info loaded."</T><T c="p">,</T>{'\n'}
              {'  '}<T c="k">"data"</T><T c="p">: {'{'}</T>{'\n'}
              {'    '}<T c="k">"merchant_token"</T><T c="p">:</T>{' '}<T c="s">"downtown"</T><T c="p">,</T>{'       '}<T c="c">{'// your alias for this merchant — see Merchant Tokens'}</T>{'\n'}
              {'    '}<T c="k">"order"</T><T c="p">: {'{'}</T>{'\n'}
              {'      '}<T c="k">"id"</T><T c="p">:</T>{'             '}<T c="n">489727</T><T c="p">,</T>{'\n'}
              {'      '}<T c="k">"placed_on"</T><T c="p">:</T>{'     '}<T c="n">1745412125</T><T c="p">,</T>{'   '}<T c="c">{'// unix: when customer placed order'}</T>{'\n'}
              {'      '}<T c="k">"status"</T><T c="p">:</T>{'        '}<T c="s">"accepted"</T><T c="p">,</T>{'\n'}
              {'      '}<T c="k">"status_updated"</T><T c="p">:</T>{' '}<T c="n">1745412300</T><T c="p">,</T>{'   '}<T c="c">{'// unix: last status change'}</T>{'\n'}
              {'      '}<T c="k">"type"</T><T c="p">:</T>{'          '}<T c="s">"delivery"</T><T c="p">,</T>{'   '}<T c="c">{'// "delivery" | "pickup"'}</T>{'\n'}
              {'      '}<T c="k">"when"</T><T c="p">:</T>{'          '}<T c="s">"now"</T><T c="p">,</T>{'        '}<T c="c">{'// "now" | "scheduled"'}</T>{'\n'}
              {'      '}<T c="k">"payment"</T><T c="p">: {'{'}</T>{'\n'}
              {'        '}<T c="k">"method"</T><T c="p">:</T>{' '}<T c="s">"cash"</T><T c="p">,</T>{'\n'}
              {'        '}<T c="k">"amount"</T><T c="p">:</T>{' '}<T c="n">600</T>{'\n'}
              {'      '}<T c="p">{'}'}</T><T c="p">,</T>{'\n'}
              {'      '}<T c="k">"customer"</T><T c="p">: {'{'}</T>{'\n'}
              {'        '}<T c="k">"name"</T><T c="p">:</T>{'  '}<T c="s">"John Doe"</T><T c="p">,</T>{'\n'}
              {'        '}<T c="k">"phone"</T><T c="p">:</T>{' '}<T c="s">"+5978123456"</T>{'\n'}
              {'      '}<T c="p">{'}'}</T><T c="p">,</T>{'\n'}
              {'      '}<T c="k">"delivery"</T><T c="p">: {'{'}</T>{'               '}<T c="c">{'// null for pickup orders'}</T>{'\n'}
              {'        '}<T c="k">"address"</T><T c="p">:</T>{'  '}<T c="s">"Henck Arronstraat 12"</T><T c="p">,</T>{'\n'}
              {'        '}<T c="k">"coords"</T><T c="p">: {'{'}</T>{' '}<T c="k">"lat"</T><T c="p">:</T>{' '}<T c="n">5.8332</T><T c="p">,</T>{' '}<T c="k">"lng"</T><T c="p">:</T>{' '}<T c="n">-55.1668</T>{' '}<T c="p">{'}'}</T><T c="p">,</T>{'\n'}
              {'        '}<T c="k">"comments"</T><T c="p">:</T>{' '}<T c="s">"Ring the bell"</T><T c="p">,</T>{'\n'}
              {'        '}<T c="k">"fee"</T><T c="p">:</T>{'      '}<T c="n">80</T><T c="p">,</T>{'\n'}
              {'        '}<T c="k">"distance"</T><T c="p">:</T>{' '}<T c="n">2.4</T>{'\n'}
              {'      '}<T c="p">{'}'}</T><T c="p">,</T>{'\n'}
              {'      '}<T c="k">"biker"</T><T c="p">: {'{'}</T>{'                   '}<T c="c">{'// null if not yet assigned'}</T>{'\n'}
              {'        '}<T c="k">"name"</T><T c="p">:</T>{'   '}<T c="s">"Carlos R."</T><T c="p">,</T>{'\n'}
              {'        '}<T c="k">"number"</T><T c="p">:</T>{' '}<T c="s">"+5977654321"</T><T c="p">,</T>{'\n'}
              {'        '}<T c="k">"photo"</T><T c="p">:</T>{'  '}<T c="s">"https://..."</T>{'\n'}
              {'      '}<T c="p">{'}'}</T><T c="p">,</T>{'\n'}
              {'      '}<T c="k">"items"</T><T c="p">: [</T>{'\n'}
              {'        '}<T c="p">{'{'}</T>{'\n'}
              {'          '}<T c="k">"title"</T><T c="p">:</T>{'        '}<T c="s">"Chicken Burger"</T><T c="p">,</T>{'\n'}
              {'          '}<T c="k">"key"</T><T c="p">:</T>{'          '}<T c="s">"chicken-burger"</T><T c="p">,</T>{'  '}<T c="c">{'// null if no custom key set'}</T>{'\n'}
              {'          '}<T c="k">"variant_key"</T><T c="p">:</T>{'   '}<T c="s">"large"</T><T c="p">,</T>{'    '}<T c="c">{'// null if no custom key set'}</T>{'\n'}
              {'          '}<T c="k">"variant"</T><T c="p">:</T>{'      '}<T c="s">"Large"</T><T c="p">,</T>{'\n'}
              {'          '}<T c="k">"qty"</T><T c="p">:</T>{'          '}<T c="n">2</T><T c="p">,</T>{'\n'}
              {'          '}<T c="k">"total"</T><T c="p">:</T>{'        '}<T c="n">140</T><T c="p">,</T>{'\n'}
              {'          '}<T c="k">"modifiers"</T><T c="p">: [</T>{'\n'}
              {'            '}<T c="p">{'{'}</T>{'\n'}
              {'              '}<T c="k">"group"</T><T c="p">:</T>{'     '}<T c="s">"Extras"</T><T c="p">,</T>{'\n'}
              {'              '}<T c="k">"group_key"</T><T c="p">:</T>{' '}<T c="s">"extras"</T><T c="p">,</T>{'  '}<T c="c">{'// null if no custom key set'}</T>{'\n'}
              {'              '}<T c="k">"choices"</T><T c="p">: [</T>{'\n'}
              {'                '}<T c="p">{'{'}</T>{' '}<T c="k">"title"</T><T c="p">:</T>{' '}<T c="s">"Extra Sauce"</T><T c="p">,</T>{' '}<T c="k">"key"</T><T c="p">:</T>{' '}<T c="s">"extra-sauce"</T>{' '}<T c="p">{'}'}</T><T c="p">,</T>{'\n'}
              {'                '}<T c="p">{'{'}</T>{' '}<T c="k">"title"</T><T c="p">:</T>{' '}<T c="s">"No Onions"</T><T c="p">,</T>{'   '}<T c="k">"key"</T><T c="p">:</T>{' '}<T c="b">null</T>{'        '}<T c="p">{'}'}</T>{'\n'}
              {'              '}<T c="p">]</T>{'\n'}
              {'            '}<T c="p">{'}'}</T>{'\n'}
              {'          '}<T c="p">]</T><T c="p">,</T>{'\n'}
              {'          '}<T c="k">"instructions"</T><T c="p">:</T>{' '}<T c="s">"Well done please"</T>{'\n'}
              {'        '}<T c="p">{'}'}</T>{'\n'}
              {'      '}<T c="p">]</T><T c="p">,</T>{'\n'}
              {'      '}<T c="k">"totals"</T><T c="p">: {'{'}</T>{'\n'}
              {'        '}<T c="k">"curr"</T><T c="p">:</T>{'     '}<T c="s">"SRD"</T><T c="p">,</T>{'\n'}
              {'        '}<T c="k">"order"</T><T c="p">:</T>{'    '}<T c="n">420</T><T c="p">,</T>{'\n'}
              {'        '}<T c="k">"delivery"</T><T c="p">:</T>{' '}<T c="n">80</T><T c="p">,</T>{'\n'}
              {'        '}<T c="k">"total"</T><T c="p">:</T>{'    '}<T c="n">521</T><T c="p">,</T>{'\n'}
              {'        '}<T c="k">"fees"</T><T c="p">: [</T>{'\n'}
              {'          '}<T c="p">{'{'}</T>{' '}<T c="k">"type"</T><T c="p">:</T>{' '}<T c="s">"checkout_fees_delivery"</T><T c="p">,</T>{'    '}<T c="k">"amount"</T><T c="p">:</T>{' '}<T c="n">80</T>{' '}<T c="p">{'}'}</T><T c="p">,</T>{'\n'}
              {'          '}<T c="p">{'{'}</T>{' '}<T c="k">"type"</T><T c="p">:</T>{' '}<T c="s">"checkout_fees_service_fee"</T><T c="p">,</T>{' '}<T c="k">"amount"</T><T c="p">:</T>{' '}<T c="n">21</T>{' '}<T c="p">{'}'}</T>{'\n'}
              {'        '}<T c="p">]</T>{'\n'}
              {'      '}<T c="p">{'}'}</T>{'\n'}
              {'    '}<T c="p">{'}'}</T>{'\n'}
              {'  '}<T c="p">{'}'}</T>{'\n'}
              <T c="p">{'}'}</T>
            </CodeBlock>

            <h3>Fields</h3>
            <DocTable
              headers={['Field', 'Type', 'Description']}
              rows={[
                ['placed_on', 'int', 'Unix timestamp when the customer placed the order'],
                ['status', 'string', 'Current order status'],
                ['status_updated', 'int', 'Unix timestamp of the last status change'],
                ['type', 'string', <><code>delivery</code> or <code>pickup</code></>],
                ['when', 'string', <><code>now</code> or <code>scheduled</code></>],
                ['delivery', 'object | null', <>Address, coords, comments, fee, distance. <code>null</code> for pickup orders</>],
                ['biker', 'object | null', <>Assigned biker. <code>null</code> if not yet assigned</>],
                ['items[].key', 'string | null', <>Custom key for the item set in the developer portal. <code>null</code> if not configured</>],
                ['items[].variant_key', 'string | null', <>Custom key for the selected variant. <code>null</code> if not configured</>],
                ['items[].modifiers[].group_key', 'string | null', <>Custom key for the modifier group. <code>null</code> if not configured</>],
                ['items[].modifiers[].choices[]', 'object', <><code>{'{ title, key }'}</code> — choice name and its custom key (<code>null</code> if not set)</>],
                ['totals.order', 'float', 'Items subtotal'],
                ['totals.delivery', 'float', 'Delivery fee only'],
                ['totals.total', 'float', 'Amount the customer pays (items + all fees + discounts)'],
                ['totals.fees', 'array', <>Breakdown of all applied fees. A negative <code>amount</code> means a coupon or discount</>],
              ]}
            />

            <h3>Errors</h3>
            <DocTable
              headers={['Response message', 'Cause']}
              rows={[
                ['Order not found or access denied.', "ID doesn't exist or belongs to another place"],
                ['No valid order ID(s) provided.', <><code>id</code> / <code>ids</code> missing or all zero</>],
              ]}
            />
          </EndpointCard>
        </section>

        {/* /order/push */}
        <section id="order-push">
          <h2>POST /order/push</h2>
          <EndpointCard method="POST" path="https://ride-eats.com/api2/sync/order/push">
            <p className="endpoint-desc">
              Advance an order through its status lifecycle. Transitions are strictly enforced; duplicate,
              skipped, or out-of-order pushes are rejected.
            </p>

            <h3>Accepted status values</h3>
            <DocTable
              headers={['Send', 'Stored internally', 'Use when']}
              rows={[
                [<Badge color="accepted">accepted</Badge>, <code>accepted</code>, 'You confirm the order'],
                [<Badge color="readydel">ready</Badge>, <><code>readydel</code> / <code>readypick</code></>, 'Food is ready — API auto-detects delivery vs pickup'],
                [<Badge color="ontheway">ontheway</Badge>, <code>ontheway</code>, <><em>delivery only</em> — biker picked up</>],
                [<Badge color="completed">completed</Badge>, <code>completed</code>, 'Order delivered or picked up by customer'],
              ]}
            />

            <Callout warn>
              Never send <code>readydel</code> or <code>readypick</code> directly. Always send{' '}
              <code>ready</code> — the API resolves the correct internal value from the order type.
            </Callout>

            <h3>Request: basic</h3>
            <CodeBlock lang="JSON">
              <T c="p">{'{'}</T>{'\n'}
              {'  '}<T c="k">"id"</T><T c="p">:</T>{'     '}<T c="n">489727</T><T c="p">,</T>{'\n'}
              {'  '}<T c="k">"status"</T><T c="p">:</T>{' '}<T c="s">"accepted"</T>{'\n'}
              <T c="p">{'}'}</T>
            </CodeBlock>

            <h3>Request: with ETA <span style={{fontSize:'13px',color:'var(--muted)',fontWeight:400}}>(accepted only)</span></h3>
            <CodeBlock lang="JSON">
              <T c="p">{'{'}</T>{'\n'}
              {'  '}<T c="k">"id"</T><T c="p">:</T>{'     '}<T c="n">489727</T><T c="p">,</T>{'\n'}
              {'  '}<T c="k">"status"</T><T c="p">:</T>{' '}<T c="s">"accepted"</T><T c="p">,</T>{'\n'}
              {'  '}<T c="k">"data"</T><T c="p">: {'{'}</T>{'\n'}
              {'    '}<T c="k">"time"</T><T c="p">:</T>{' '}<T c="n">25</T>{'  '}<T c="c">{'// estimated preparation time in minutes'}</T>{'\n'}
              {'  '}<T c="p">{'}'}</T>{'\n'}
              <T c="p">{'}'}</T>
            </CodeBlock>

            <h3>Response</h3>
            <CodeBlock lang="JSON">
              <T c="p">{'{'}</T>{'\n'}
              {'  '}<T c="k">"success"</T><T c="p">:</T>{'  '}<T c="b">true</T><T c="p">,</T>{'\n'}
              {'  '}<T c="k">"response"</T><T c="p">:</T>{' '}<T c="s">"Order status updated successfully."</T><T c="p">,</T>{'\n'}
              {'  '}<T c="k">"data"</T><T c="p">: {'{'}</T>{'\n'}
              {'    '}<T c="k">"order_id"</T><T c="p">:</T>{'        '}<T c="n">489727</T><T c="p">,</T>{'\n'}
              {'    '}<T c="k">"previous_status"</T><T c="p">:</T>{' '}<T c="s">"pending"</T><T c="p">,</T>{'\n'}
              {'    '}<T c="k">"new_status"</T><T c="p">:</T>{'      '}<T c="s">"accepted"</T><T c="p">,</T>{'\n'}
              {'    '}<T c="k">"extras"</T><T c="p">: {'{'}</T>{' '}<T c="k">"time"</T><T c="p">:</T>{' '}<T c="n">25</T>{' '}<T c="p">{'}'}</T>{'          '}<T c="c">{'// echoed back; null if not sent'}</T>{'\n'}
              {'  '}<T c="p">{'}'}</T>{'\n'}
              <T c="p">{'}'}</T>
            </CodeBlock>

            <h3>Errors</h3>
            <DocTable
              headers={['Response message', 'Cause']}
              rows={[
                ["Order is currently 'accepted'. Expected next status: 'ready'.", 'Wrong next step for current status'],
                ["Order is already 'completed' and cannot be advanced further.", 'Order is in a terminal state'],
                ['Invalid status. Allowed: accepted, ready, ontheway, completed.', 'Unrecognised status value sent'],
                ['Order not found or access denied.', "ID doesn't exist or belongs to another place"],
              ]}
            />
          </EndpointCard>
        </section>

        {/* MERCHANT TOKENS */}
        <section id="merchant-refs">
          <h2>Merchant Tokens</h2>
          <p>
            Every webhook payload and every <code>/order/info</code> response includes a{' '}
            <code>merchant_token</code> field. This is the <strong>alias you chose</strong> when linking a
            merchant to your API key — human-readable, meaningful to your system, and never an internal
            database ID.
          </p>
          <Callout>
            <strong>Why not the merchant ID?</strong> Internal numeric IDs are implementation details that can
            change and expose information about the platform. Your alias (<code>merchant_token</code>) is set by
            you, scoped to your API key, and maps directly to your own internal location names — no lookup table
            needed.
          </Callout>

          <h3>Format</h3>
          <CodeBlock lang="Example">
            <T c="s">"merchant_token"</T><T c="p">:</T>{' '}<T c="s">"downtown"</T>
          </CodeBlock>

          <h3>Setting your alias</h3>
          <p>
            When linking a merchant to your API key on the developer portal, you assign a short alias to each
            location. That alias becomes the <code>merchant_token</code> in every response and webhook event for
            that merchant.
          </p>
          <DocTable
            headers={['Merchant name (Ride Eats)', 'Your alias → merchant_token']}
            rows={[
              ['KFC Paramaribo Centrum', <code>downtown</code>],
              ['KFC Airport',           <code>airport</code>],
            ]}
          />

          <h3>Properties</h3>
          <DocTable
            headers={['Property', 'Details']}
            rows={[
              ['Human-readable', <>You define it — use any name meaningful to your system (<code>downtown</code>, <code>pos-1</code>, <code>branch-north</code>).</>],
              ['Stable', 'The alias never changes unless you edit it in the portal.'],
              ['Scoped', 'Unique within your API key. The same merchant can have a different alias under a different key.'],
              ['No reverse lookup needed', 'Because you set the value yourself, there is no lookup table — the token is already your internal identifier.'],
            ]}
          />

          <h3>Usage in webhooks</h3>
          <p>Because the token is your own alias, you can route directly — no mapping step required:</p>
          <CodeBlock lang="Pseudo-code">
            <T c="c">{'// incoming webhook'}</T>{'\n'}
            <T c="k">token</T>{' '}<T c="p">=</T>{' payload.'}<T c="k">data</T>.merchant_token{'   '}<T c="c">{'// "downtown"'}</T>{'\n\n'}
            <T c="k">if</T>{' token '}<T c="p">==</T>{' '}<T c="s">"downtown"</T>{':\n'}
            {'    pos_downtown.'}<T c="k">push</T>{'(payload)\n'}
            <T c="k">elif</T>{' token '}<T c="p">==</T>{' '}<T c="s">"airport"</T>{':\n'}
            {'    pos_airport.'}<T c="k">push</T>{'(payload)\n'}
            <T c="k">else</T>{':\n'}
            {'    '}<T c="k">return</T>{' '}<T c="n">400</T>{'   '}<T c="c">{'// unknown merchant'}</T>
          </CodeBlock>
        </section>

        {/* STATUS FLOW */}
        <section id="status-flow">
          <h2>Status Flow</h2>
          <p>Orders always move forward. You cannot skip a step, repeat a status, or go backwards.</p>

          <div className="flow-grid">
            <div className="flow-col">
              <h3>Delivery order</h3>
              <div className="flow">
                <div className="flow-row"><Badge color="pending">pending</Badge><span className="flow-note">customer places order</span></div>
                <div className="flow-row"><span className="flow-arrow">↓</span><span style={{fontSize:'12px',color:'var(--muted)'}}>send</span>&nbsp;<code>accepted</code></div>
                <div className="flow-row"><Badge color="accepted">accepted</Badge></div>
                <div className="flow-row"><span className="flow-arrow">↓</span><span style={{fontSize:'12px',color:'var(--muted)'}}>send</span>&nbsp;<code>ready</code></div>
                <div className="flow-row"><Badge color="readydel">readydel</Badge></div>
                <div className="flow-row"><span className="flow-arrow">↓</span><span style={{fontSize:'12px',color:'var(--muted)'}}>send</span>&nbsp;<code>ontheway</code></div>
                <div className="flow-row"><Badge color="ontheway">ontheway</Badge></div>
                <div className="flow-row"><span className="flow-arrow">↓</span><span style={{fontSize:'12px',color:'var(--muted)'}}>send</span>&nbsp;<code>completed</code></div>
                <div className="flow-row"><Badge color="completed">completed</Badge></div>
              </div>
            </div>

            <div className="flow-col">
              <h3>Pickup order</h3>
              <div className="flow">
                <div className="flow-row"><Badge color="pending">pending</Badge><span className="flow-note">customer places order</span></div>
                <div className="flow-row"><span className="flow-arrow">↓</span><span style={{fontSize:'12px',color:'var(--muted)'}}>send</span>&nbsp;<code>accepted</code></div>
                <div className="flow-row"><Badge color="accepted">accepted</Badge></div>
                <div className="flow-row"><span className="flow-arrow">↓</span><span style={{fontSize:'12px',color:'var(--muted)'}}>send</span>&nbsp;<code>ready</code></div>
                <div className="flow-row"><Badge color="readypick">readypick</Badge></div>
                <div className="flow-row"><span className="flow-arrow">↓</span><span style={{fontSize:'12px',color:'var(--muted)'}}>send</span>&nbsp;<code>completed</code></div>
                <div className="flow-row"><Badge color="completed">completed</Badge></div>
              </div>
            </div>
          </div>
        </section>

        {/* ERROR REFERENCE */}
        <section id="errors">
          <h2>Error Reference</h2>
          <p>
            All errors return <code>"success": false</code> and a human-readable <code>response</code> string.
          </p>
          <DocTable
            headers={['HTTP', 'Response message', 'Cause']}
            rows={[
              ['200', 'Please provide a valid place API key.', <><code>API</code> header or <code>endpoint</code> param missing</>],
              ['200', 'Invalid or inactive API key.', "Key doesn't exist or is inactive"],
              ['200', 'Unknown endpoint.', 'Endpoint path not recognised'],
              ['200', 'Missing required field: external_order_id.', <><code>/order/create</code> was called without an idempotency ID</>],
              ['200', 'Merchant token not found or access denied.', <><code>merchant_token</code> is unknown for this API key</>],
              ['200', 'Unknown item key at items[x].', <><code>/order/create</code> item key is not configured or does not belong to the merchant</>],
              ['200', 'Order not found or access denied.', 'Order ID not found or belongs to another place'],
              ['200', 'No valid order ID(s) provided.', <><code>id</code>/<code>ids</code> missing or all zeros</>],
              ['200', 'Invalid status. Allowed: accepted, ready, ontheway, completed.', <>Unrecognised value sent to <code>/push</code></>],
              ['200', "Order is currently '{x}'. Expected next status: '{y}'.", 'Wrong transition step'],
              ['200', "Order is already '{x}' and cannot be advanced further.", 'Order is in a terminal state'],
            ]}
          />
          <Callout>
            The API always returns HTTP 200. Check <code>success: true/false</code> in the response body to
            determine the outcome.
          </Callout>
        </section>

        {/* EXAMPLES */}
        <section id="examples">
          <h2>Examples</h2>

          <h3>Delivery order: full flow</h3>
          <CodeBlock lang="HTTP">
            <T c="c">{'# 1. Fetch the order'}</T>{'\n'}
            <T c="hd">POST</T>{' '}<T c="hu">https://ride-eats.com/api2/sync/order/info</T>{'\n'}
            <T c="hl">API: your-api-key-here</T>{'\n'}
            <T c="hl">Content-Type: application/json</T>{'\n\n'}
            <T c="p">{'{'}</T>{' '}<T c="k">"id"</T><T c="p">:</T>{' '}<T c="n">489727</T>{' '}<T c="p">{'}'}</T>{'\n\n\n'}
            <T c="c">{'# 2. Accept with 20-minute ETA'}</T>{'\n'}
            <T c="hd">POST</T>{' '}<T c="hu">https://ride-eats.com/api2/sync/order/push</T>{'\n'}
            <T c="hl">API: your-api-key-here</T>{'\n'}
            <T c="hl">Content-Type: application/json</T>{'\n\n'}
            <T c="p">{'{'}</T>{' '}<T c="k">"id"</T><T c="p">:</T>{' '}<T c="n">489727</T><T c="p">,</T>{' '}<T c="k">"status"</T><T c="p">:</T>{' '}<T c="s">"accepted"</T><T c="p">,</T>{' '}<T c="k">"data"</T><T c="p">: {'{'}</T>{' '}<T c="k">"time"</T><T c="p">:</T>{' '}<T c="n">20</T>{' '}<T c="p">{'}'} {'}'}</T>{'\n\n\n'}
            <T c="c">{'# 3. Food is ready for biker pickup'}</T>{'\n'}
            <T c="hd">POST</T>{' '}<T c="hu">https://ride-eats.com/api2/sync/order/push</T>{'\n\n'}
            <T c="p">{'{'}</T>{' '}<T c="k">"id"</T><T c="p">:</T>{' '}<T c="n">489727</T><T c="p">,</T>{' '}<T c="k">"status"</T><T c="p">:</T>{' '}<T c="s">"ready"</T>{' '}<T c="p">{'}'}</T>{'\n\n\n'}
            <T c="c">{'# 4. Biker picked up'}</T>{'\n'}
            <T c="hd">POST</T>{' '}<T c="hu">https://ride-eats.com/api2/sync/order/push</T>{'\n\n'}
            <T c="p">{'{'}</T>{' '}<T c="k">"id"</T><T c="p">:</T>{' '}<T c="n">489727</T><T c="p">,</T>{' '}<T c="k">"status"</T><T c="p">:</T>{' '}<T c="s">"ontheway"</T>{' '}<T c="p">{'}'}</T>{'\n\n\n'}
            <T c="c">{'# 5. Delivered'}</T>{'\n'}
            <T c="hd">POST</T>{' '}<T c="hu">https://ride-eats.com/api2/sync/order/push</T>{'\n\n'}
            <T c="p">{'{'}</T>{' '}<T c="k">"id"</T><T c="p">:</T>{' '}<T c="n">489727</T><T c="p">,</T>{' '}<T c="k">"status"</T><T c="p">:</T>{' '}<T c="s">"completed"</T>{' '}<T c="p">{'}'}</T>
          </CodeBlock>

          <h3>Pickup order: full flow</h3>
          <CodeBlock lang="HTTP">
            <T c="c">{'# 1. Accept'}</T>{'\n'}
            <T c="hd">POST</T>{' '}<T c="hu">https://ride-eats.com/api2/sync/order/push</T>{'\n'}
            <T c="hl">API: your-api-key-here</T>{'\n'}
            <T c="hl">Content-Type: application/json</T>{'\n\n'}
            <T c="p">{'{'}</T>{' '}<T c="k">"id"</T><T c="p">:</T>{' '}<T c="n">489728</T><T c="p">,</T>{' '}<T c="k">"status"</T><T c="p">:</T>{' '}<T c="s">"accepted"</T>{' '}<T c="p">{'}'}</T>{'\n\n\n'}
            <T c="c">{'# 2. Ready for customer'}</T>{'\n'}
            <T c="hd">POST</T>{' '}<T c="hu">https://ride-eats.com/api2/sync/order/push</T>{'\n\n'}
            <T c="p">{'{'}</T>{' '}<T c="k">"id"</T><T c="p">:</T>{' '}<T c="n">489728</T><T c="p">,</T>{' '}<T c="k">"status"</T><T c="p">:</T>{' '}<T c="s">"ready"</T>{' '}<T c="p">{'}'}</T>{'\n\n\n'}
            <T c="c">{'# 3. Picked up'}</T>{'\n'}
            <T c="hd">POST</T>{' '}<T c="hu">https://ride-eats.com/api2/sync/order/push</T>{'\n\n'}
            <T c="p">{'{'}</T>{' '}<T c="k">"id"</T><T c="p">:</T>{' '}<T c="n">489728</T><T c="p">,</T>{' '}<T c="k">"status"</T><T c="p">:</T>{' '}<T c="s">"completed"</T>{' '}<T c="p">{'}'}</T>
          </CodeBlock>

          <h3>Batch fetch</h3>
          <CodeBlock lang="HTTP">
            <T c="hd">POST</T>{' '}<T c="hu">https://ride-eats.com/api2/sync/order/info</T>{'\n'}
            <T c="hl">API: your-api-key-here</T>{'\n'}
            <T c="hl">Content-Type: application/json</T>{'\n\n'}
            <T c="p">{'{'}</T>{' '}<T c="k">"ids"</T><T c="p">:</T>{' '}<T c="p">[</T><T c="n">489727</T><T c="p">,</T>{' '}<T c="n">489728</T><T c="p">,</T>{' '}<T c="n">489730</T><T c="p">]</T>{' '}<T c="p">{'}'}</T>
          </CodeBlock>
        </section>

        </div>
      </div>
    </div>
  );
}
